/**
 * utils.ts — Helper cn() para composición de clases Tailwind
 *
 * Patrón: implementación mínima de clsx inline (evita dep adicional) +
 * tailwind-merge para resolver conflictos de utilidades Tailwind.
 *
 * Por qué inline en vez de instalar clsx:
 *   El handoff autoriza exactamente 12 deps nuevas (ADR-025 + ADR-026).
 *   tailwind-merge v3 fue la versión que npm resolvió al instalar; la API
 *   pública (twMerge) es compatible. Pin controlado recomendado en próximo
 *   sprint: npm pin tailwind-merge@3.x.x en package.json.
 *
 * Uso:
 *   cn('px-2 text-sm', condition && 'font-bold', 'text-lg')
 *   → 'px-2 font-bold text-lg'  (text-sm resuelto por tailwind-merge)
 */
import { twMerge } from 'tailwind-merge';

// Tipo compatible con el patrón clsx: acepta strings, false, null, undefined
type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, boolean | null | undefined>
  | ClassValue[];

/**
 * Implementación mínima de clsx sin dependencia externa.
 * Aplana arrays y filtra falsy; los objetos usan la clave si el valor es truthy.
 */
function clsxInternal(...inputs: ClassValue[]): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === 'string' || typeof input === 'number') {
      classes.push(String(input));
    } else if (Array.isArray(input)) {
      const nested = clsxInternal(...input);
      if (nested) classes.push(nested);
    } else if (typeof input === 'object') {
      for (const [key, value] of Object.entries(input)) {
        if (value) classes.push(key);
      }
    }
  }

  return classes.join(' ');
}

/**
 * cn — Composer de clases para componentes UI.
 *
 * Combina clsxInternal (condicionales, arrays, objetos) con tailwind-merge
 * (resolver conflictos Tailwind como 'px-2 px-4' → 'px-4').
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsxInternal(...inputs));
}

export type { ClassValue };
